class Quiz {
  constructor() {
    this.currentStep = 1;
    this.totalSteps = 4;
    this.showingResults = false;
    this.form = document.getElementById('quiz-form');
    this.selectionOrder = {}; // Track selection order for each step
    this.customerId = this.form?.dataset?.customerId || null;
    this.init();
  }

  init() {
    this.preselectSavedAnswers();
    this.bindEvents();
    this.bindLabelEvents();
    this.updateUI();
  }

  preselectSavedAnswers() {
    // Pre-select checkboxes/radios based on saved customer preferences
    const containers = this.form.querySelectorAll('.quiz-select-container');
    
    containers.forEach(container => {
      const savedTitles = container.dataset.savedTitles || '';
      if (!savedTitles) return;
      
      const savedTitlesArray = savedTitles.split(',').map(t => t.trim()).filter(Boolean);
      const inputs = container.querySelectorAll('.quiz-input');
      
      inputs.forEach(input => {
        const title = (input.dataset.title || '').trim();
        if (title && savedTitlesArray.includes(title)) {
          input.checked = true;
          
          // Track selection order
          const stepNumber = parseInt(container.dataset.step, 10);
          this.addSelectionOrder(stepNumber, input);
        }
      });
    });
  }

  bindLabelEvents() {
    // Add click handlers to all quiz option labels
    const labels = this.form.querySelectorAll('label.quiz-option');
    labels.forEach(label => {
      let wasChecked = false;
      
      label.addEventListener('mousedown', () => {
        const input = label.querySelector('.quiz-input');
        wasChecked = input ? input.checked : false;
      });
      
      label.addEventListener('click', (e) => {
        const input = label.querySelector('.quiz-input');
        
        // Handle checkbox validations
        if (input && input.type === 'checkbox') {
          const stepNumber = this.currentStep;
          const stepInputs = this.form.querySelectorAll(`.quiz-step[data-step="${stepNumber}"] .quiz-input`);
          const maxSelections = parseInt(input.dataset.max) || 1;
          const isReset = input.dataset.isReset === 'true';
          
          // If clicking a checked checkbox, allow uncheck
          if (wasChecked && input.checked) {
            e.preventDefault();
            input.checked = false;
            this.removeSelectionOrder(stepNumber, input);
            this.updateUI();
            return;
          }
          
          // If this is being checked
          if (!wasChecked && input.checked) {
            if (isReset) {
              // Reset button: uncheck all others
              stepInputs.forEach(inp => {
                if (inp !== input) {
                  inp.checked = false;
                  this.removeSelectionOrder(stepNumber, inp);
                }
              });
              this.addSelectionOrder(stepNumber, input);
            } else {
              // Regular option: uncheck reset button
              stepInputs.forEach(inp => {
                if (inp.dataset.isReset === 'true') {
                  inp.checked = false;
                  this.removeSelectionOrder(stepNumber, inp);
                }
              });
              
              // Check max selections
              const checkedInputs = Array.from(stepInputs).filter(inp => inp.checked && inp !== input);
              if (checkedInputs.length >= maxSelections) {
                e.preventDefault();
                input.checked = false;
                return;
              }
              
              this.addSelectionOrder(stepNumber, input);
            }
          }
        } else if (input && wasChecked && input.checked && input.type === 'radio') {
          // Allow unchecking radio buttons
          e.preventDefault();
          input.checked = false;
          this.removeSelectionOrder(this.currentStep, input);
        } else if (input && !wasChecked && input.checked && input.type === 'radio') {
          // Track radio selection
          this.addSelectionOrder(this.currentStep, input);
        }
        
        this.updateUI();
      });
    });
  }

  bindEvents() {
    // Form input changes
    this.form.addEventListener('change', (e) => {
      if (e.target.classList.contains('quiz-input')) {
        this.updateUI();
      }
    });

    // Button clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('.quiz-btn-next')) {
        this.nextStep();
      } else if (e.target.closest('.quiz-btn-back')) {
        this.prevStep();
      } else if (e.target.classList.contains('quiz-btn-restart')) {
        this.restart();
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' && this.canGoNext()) this.nextStep();
      if (e.key === 'ArrowLeft' && this.canGoBack()) this.prevStep();
    });
  }

  getFormData() {
    const formData = new FormData(this.form);
    const answers = {};
    const metafields = {
      flavours: [],
      categories: [],
      features: [],
      availability_period: []
    };
    
    for (let i = 1; i <= this.totalSteps; i++) {
      const stepName = `step${i}`;
      const stepAnswers = formData.getAll(stepName);
      
      if (stepAnswers.length > 0) {
        answers[i] = stepAnswers.length === 1 ? stepAnswers[0] : stepAnswers;
        
        // Collect metafields from checked inputs
        const checkedInputs = this.form.querySelectorAll(`input[name="${stepName}"]:checked`);
        checkedInputs.forEach((input, idx) => {          
          // Flavours: "Tropical,Caramel" -> split by comma
          const flavoursValue = input.dataset.metafieldFlavours;
          if (flavoursValue && flavoursValue !== 'null' && flavoursValue.trim() !== '') {
            const flavours = flavoursValue.split(',').map(f => f.trim()).filter(Boolean);
            flavours.forEach(flavour => {
              if (!metafields.flavours.includes(flavour)) {
                metafields.flavours.push(flavour);
              }
            });
          }
          
          // Categories: "Accessories,Coffee Brewing Equipment" -> split by comma
          const categoriesValue = input.dataset.metafieldCategories;
          if (categoriesValue && categoriesValue !== 'null' && categoriesValue.trim() !== '') {
            const categories = categoriesValue.split(',').map(c => c.trim()).filter(Boolean);
            categories.forEach(category => {
              if (!metafields.categories.includes(category)) {
                metafields.categories.push(category);
              }
            });
          }
          
          // Features: "Championship Selection,Budget Friendly" -> split by comma
          const featuresValue = input.dataset.metafieldFeatures;
          if (featuresValue && featuresValue !== 'null' && featuresValue.trim() !== '') {
            const features = featuresValue.split(',').map(f => f.trim()).filter(Boolean);
            features.forEach(feature => {
              if (!metafields.features.includes(feature)) {
                metafields.features.push(feature);
              }
            });
          }
          
          // Availability Period: "Seasonal Coffee,House Coffee" -> split by comma
          const periodValue = input.dataset.metafieldAvailabilityPeriod;
          if (periodValue && periodValue !== 'null' && periodValue.trim() !== '') {
            const periods = periodValue.split(',').map(p => p.trim()).filter(Boolean);
            periods.forEach(period => {
              if (!metafields.availability_period.includes(period)) {
                metafields.availability_period.push(period);
              }
            });
          }
        });
      }
    }
    
    return { answers, metafields };
  }

  nextStep() {
    if (!this.canGoNext()) return;

    if (this.currentStep === this.totalSteps) {
      this.showResults();
    } else {
      this.currentStep++;
      this.showStep(this.currentStep);
      this.updateUI();
    }
  }

  prevStep() {
    if (!this.canGoBack()) return;

    if (this.showingResults) {
      this.hideResults();
      this.currentStep = this.totalSteps;
    } else {
      this.currentStep--;
    }
    
    this.showStep(this.currentStep);
    this.updateUI();
  }

  showStep(stepNumber) {
    // Hide all steps and show current
    document.querySelectorAll('.quiz-step').forEach(step => step.classList.remove('active'));
    document.querySelector(`[data-step="${stepNumber}"]`)?.classList.add('active');

    // Update question title
    this.updateQuestionTitle(stepNumber);
    
    // Initialize selection order for this step if not already done
    this.initializeStepSelectionOrder(stepNumber);
  }

  initializeStepSelectionOrder(stepNumber) {
    // If selection order already exists for this step, skip
    if (this.selectionOrder[stepNumber] && this.selectionOrder[stepNumber].length > 0) {
      return;
    }
    
    // Check for already checked inputs (e.g., when navigating back)
    const stepElement = this.form.querySelector(`.quiz-step[data-step="${stepNumber}"]`);
    if (!stepElement) return;
    
    const checkedInputs = stepElement.querySelectorAll('.quiz-input:checked');
    if (checkedInputs.length > 0) {
      // Initialize order array with current checked inputs
      this.selectionOrder[stepNumber] = Array.from(checkedInputs);
    }
  }

  updateUI() {
    this.updateStepCounter();
    this.updateNavigation();
    this.updateButtonTexts();
    this.updateSelectionIndicators();
  }

  updateStepCounter() {
    const currentStepSpan = document.querySelector('.current-step');
    if (currentStepSpan) currentStepSpan.textContent = this.currentStep;
  }

  updateButtonTexts() {
    // Update all quiz option buttons based on input checked state
    const labels = this.form.querySelectorAll('label.quiz-option');
    labels.forEach(label => {
      const input = label.querySelector('.quiz-input');
      const button = label.querySelector('.quiz-option-btn');
      
      if (input && button) {
        const textDefault = button.dataset.textDefault;
        const textSelected = button.dataset.textSelected;
        
        button.textContent = input.checked ? textSelected : textDefault;
      }
    });
  }

  addSelectionOrder(stepNumber, input) {
    // Initialize step array if needed
    if (!this.selectionOrder[stepNumber]) {
      this.selectionOrder[stepNumber] = [];
    }
    
    // Add input to order if not already there
    if (!this.selectionOrder[stepNumber].includes(input)) {
      this.selectionOrder[stepNumber].push(input);
    }
  }

  removeSelectionOrder(stepNumber, input) {
    if (!this.selectionOrder[stepNumber]) return;
    
    // Remove input from order array
    const index = this.selectionOrder[stepNumber].indexOf(input);
    if (index !== -1) {
      this.selectionOrder[stepNumber].splice(index, 1);
    }
  }

  updateSelectionIndicators() {
    // Update selection indicators for each step
    for (let stepNum = 1; stepNum <= this.totalSteps; stepNum++) {
      const stepElement = this.form.querySelector(`.quiz-step[data-step="${stepNum}"]`);
      if (!stepElement) continue;

      const stepInputs = stepElement.querySelectorAll('.quiz-input');
      
      // Get max selections from first input
      const firstInput = stepInputs[0];
      const maxSelections = parseInt(firstInput?.dataset?.max) || 1;
      
      // Determine indicator type
      const isSingleSelection = maxSelections === 1;
      
      // Get selection order for this step
      const stepOrder = this.selectionOrder[stepNum] || [];
      
      // Update each input's indicator
      stepInputs.forEach(input => {
        const content = input.nextElementSibling; // .quiz-option-content
        if (!content) return;

        if (input.checked) {
          if (isSingleSelection) {
            // Show checkmark for single selection
            content.setAttribute('data-selection-indicator', '✓');
          } else {
            // Show order number based on selection order array
            const orderIndex = stepOrder.indexOf(input);
            if (orderIndex !== -1) {
              content.setAttribute('data-selection-indicator', (orderIndex + 1).toString());
            }
          }
        } else {
          content.removeAttribute('data-selection-indicator');
        }
      });
    }
  }

  updateQuestionTitle(stepNumber) {
    const questionElement = document.querySelector('.quiz-question h2');
    
    if (!questionElement) {
      const altElement = document.querySelector('.quiz-question .style-h4');
      if (altElement) {
        this.updateQuestionText(altElement, stepNumber);
        return;
      }
      return;
    }

    this.updateQuestionText(questionElement, stepNumber);
  }

  updateQuestionText(element, stepNumber) {
    // Get question from data attribute
    const questionKey = `question${stepNumber}`;
    const newQuestion = element.dataset[questionKey];
    
    if (newQuestion) {
      element.textContent = newQuestion;
    }
  }

  updateNavigation() {
    const backBtn = document.querySelector('.quiz-btn-back');
    const nextBtns = document.querySelectorAll('.quiz-btn-next');

    if (backBtn) {
      backBtn.disabled = !this.canGoBack();
    }
    
    nextBtns.forEach(nextBtn => {
      if (this.showingResults) {
        nextBtn.style.display = 'none';
      } else {
        nextBtn.style.display = 'flex';
        nextBtn.disabled = !this.canGoNext();
        nextBtn.textContent = this.currentStep === this.totalSteps ? 'SEE RESULTS' : 'NEXT';
      }
    });
  }

  showResults() {
    this.showingResults = true;
    document.querySelector('.quiz-steps').style.display = 'none';
    document.querySelector('.quiz-header').style.display = 'none';
    document.querySelector('.quiz-results').style.display = 'block';

    // Load products
    this.loadRecommendedProducts();

    // Save quiz preferences if customer is logged in (silent, no alert)
    if (this.customerId && typeof saveQuizPreferences === 'function') {
      saveQuizPreferences(this.customerId);
    }
  }

  hideResults() {
    this.showingResults = false;
    document.querySelector('.quiz-steps').style.display = 'block';
    document.querySelector('.quiz-header').style.display = 'flex';
    document.querySelector('.quiz-results').style.display = 'none';
  }

  restart() {
    this.currentStep = 1;
    this.showingResults = false;
    this.selectionOrder = {};
    this.form.reset();
    this.hideResults();
    this.showStep(1);
    this.updateUI();
  }

  canGoNext() {
    const currentStepElement = this.form.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
    if (!currentStepElement) return false;
    
    const stepInputs = currentStepElement.querySelectorAll('.quiz-input');
    const checkedInputs = Array.from(stepInputs).filter(inp => inp.checked);
    
    if (checkedInputs.length === 0) return false;
    
    // Get min requirement from first input (all inputs in same step have same min/max)
    const firstInput = stepInputs[0];
    const minRequired = parseInt(firstInput?.dataset?.min) || 1;
    
    return checkedInputs.length >= minRequired;
  }

  canGoBack() {
    return this.currentStep > 1 || this.showingResults;
  }

  // Metafield configuration: defines how to extract values from each metafield type
  getMetafieldConfig() {
    return {
      availability_period: {
        field: 'availability_period',
        valueExtractor: (item) => item?.period || ''
      },
      flavours: {
        field: 'flavours',
        valueExtractor: (item) => item?.label || item?.name || ''
      },
      categories: {
        field: 'categories',
        valueExtractor: (item) => item?.category || ''
      },
      features: {
        field: 'features',
        valueExtractor: (item) => item?.name || ''
      }
    };
  }

  // Extract values from a product's metafield array
  extractMetafieldValues(productMetafields, config) {
    const metafieldData = productMetafields[config.field];
    if (!metafieldData) return [];
    
    const itemsArray = Array.isArray(metafieldData) ? metafieldData : [metafieldData];
    return itemsArray
      .map(config.valueExtractor)
      .filter(value => value !== '');
  }

  // Check if a product matches any of the required metafields
  productMatchesMetafields(productMetafields, requiredMetafields) {
    const configs = this.getMetafieldConfig();
    
    for (const [key, config] of Object.entries(configs)) {
      const requiredValues = requiredMetafields[key] || [];
      if (requiredValues.length === 0) continue;
      
      const productValues = this.extractMetafieldValues(productMetafields, config);
      const hasMatch = productValues.some(value => requiredValues.includes(value));
      
      if (hasMatch) return true;
    }
    
    return false;
  }

  // Calculate match score based on how many metafield values match
  calculateMatchScoreByMetafields(product, metafields) {
    const productMetafields = product.metafields || {};
    const configs = this.getMetafieldConfig();
    let score = 0;
    
    for (const [key, config] of Object.entries(configs)) {
      const requiredValues = metafields[key] || [];
      if (requiredValues.length === 0) continue;
      
      const productValues = this.extractMetafieldValues(productMetafields, config);
      const matches = productValues.filter(value => requiredValues.includes(value));
      score += matches.length;
    }
    
    return score;
  }

  async loadRecommendedProducts() {
    const { metafields } = this.getFormData();
    const loadingEl = document.querySelector('.quiz-products-loading');
    const gridEl = document.getElementById('quiz-products-secondary');
    
    if (!gridEl) {
      console.error('❌ [QUIZ] gridEl not found!');
      return;
    }
    
    if (loadingEl) loadingEl.style.display = 'block';
    gridEl.innerHTML = '';
    
    try {
      const hasMetafields = Object.values(metafields).some(arr => arr.length > 0);
      
      if (!hasMetafields) {
        this.showErrorMessage(gridEl, loadingEl, 'No preferences selected. Please retake the quiz.');
        return;
      }
      
      const maxPossibleScore = Object.values(metafields).reduce((sum, arr) => sum + arr.length, 0);
      const products = await this.fetchProductsByMetafields(metafields);
      
      if (products.length === 0) {
        this.showErrorMessage(gridEl, loadingEl, 'No products found matching your preferences. Try adjusting your selections.');
        return;
      }
      
      const scoredProducts = products
        .map(product => ({
          ...product,
          matchScore: this.calculateMatchScoreByMetafields(product, metafields),
          maxScore: maxPossibleScore
        }))
        .sort((a, b) => b.matchScore - a.matchScore)
        .slice(0, 4);
      
      if (loadingEl) loadingEl.style.display = 'none';
      
      if (scoredProducts.length === 0) {
        this.showErrorMessage(gridEl, null, 'No products found matching your preferences. Try adjusting your selections.');
      } else {
        await this.renderProductCards(scoredProducts, gridEl);
        this.initSwiper();
      }
      
    } catch (error) {
      console.error('Error loading products:', error);
      this.showErrorMessage(gridEl, loadingEl, 'Service temporarily unavailable. Please try again later.');
    }
  }

  showErrorMessage(gridEl, loadingEl, message) {
    if (loadingEl) loadingEl.style.display = 'none';
    gridEl.innerHTML = `<p class="text-center style-p3 text-brown1">${message}</p>`;
  }

  initSwiper() {
    new Swiper('.quiz-products-secondary', {
      slidesPerView: 1.2,
      spaceBetween: 14,
      breakpoints: {
        1280: {
          slidesPerView: 3,
          spaceBetween: 32,
        },
      },
    });
  }

  async fetchProductsByMetafields(metafields) {    
    try {
      
      const response = await fetch('/collections/all?section_id=all-products-metafields-json');
      
      if (!response.ok) {
        throw new Error(`Metafields API failed with status ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const scriptTag = doc.querySelector('script#all-products-metafields-data[type="application/json"]');
      
      if (!scriptTag) {
        console.warn('⚠️ [QUIZ] Script tag not found in response');
        console.warn('⚠️ [QUIZ] HTML preview:', html.substring(0, 500));
        return [];
      }
      
      const allProductsWithMetafields = JSON.parse(scriptTag.textContent);
      
      return allProductsWithMetafields
        .filter(product => {
          const productMetafields = product.metafields || {};
          return this.productMatchesMetafields(productMetafields, metafields);
        })
        .map(product => ({
          handle: product.handle,
          metafields: product.metafields || {}
        }));
      
    } catch (error) {
      console.error('❌ [QUIZ] Error fetching products by metafields:', error);
      console.error('❌ [QUIZ] Error stack:', error.stack);
      return [];
    }
  }

  async renderProductCards(products, gridEl) {
    const highlightContainer = document.getElementById('quiz-highlight-product');
    
    // Clear both containers
    highlightContainer.innerHTML = '';
    gridEl.innerHTML = '';
    
    // Fetch all product cards in parallel
    // First product uses highlight-product-card, others use product-card
    const cardPromises = products.map((product, index) => 
      this.fetchProductCard(product, index === 0)
    );
    
    try {
      const cards = await Promise.all(cardPromises);
      
      // Insert cards into appropriate containers
      cards.forEach((cardData, index) => {
        if (cardData.cardElement) {
          if (index === 0 && highlightContainer) {
            // First product goes to highlight container
            highlightContainer.appendChild(cardData.cardElement);
          } else {
            // Other products go to regular grid
            gridEl.appendChild(cardData.cardElement);
          }
        }
      });
    } catch (error) {
      console.error('Error rendering product cards:', error);
      gridEl.innerHTML = '<p class="text-center style-p3 text-brown1">Error loading product cards.</p>';
    }
  }

  async fetchProductCard(product, isHighlight = false) {
    try {
      const matchPercentage = Math.min(100, Math.round((product.matchScore / product.maxScore) * 100));
      
      // Use highlight-product-card for first product, product-card for others
      const sectionId = isHighlight ? 'quiz-highlight-product-card' : 'quiz-product-card';
      const response = await fetch(`/products/${product.handle}?section_id=${sectionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch product card for ${product.handle}`);
      }
      
      const html = await response.text();
      
      // Parse HTML and extract only the .quiz-result element
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cardElement = doc.querySelector('.quiz-result');
 
      if (cardElement) {
        cardElement.setAttribute('data-match-score', matchPercentage);
      }
 
      return {
        cardElement: cardElement,
        matchPercentage: matchPercentage
      };
    } catch (error) {
      console.error(`Error fetching card for ${product.handle}:`, error);
      return {
        cardElement: null,
        matchPercentage: 0
      };
    }
  }

  goToShop() {
    // Redirect to shop - filtering is now done via metafields in the quiz results
    window.location.href = '/collections/all';
  }
}

// Initialize quiz when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.quiz = new Quiz();
});